import Phaser from 'phaser';
import { HAPTICS, MAX_SIM_STEPS_PER_FRAME, PALETTE, SIM_STEP, SPRINT } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { countryFlag } from '../../game/data/flags';
import { t } from '../../game/i18n';
import { pickOpponents } from '../../game/data/aiRoster';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { TapZones } from '../../ui/TapZones';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Side = 'L' | 'R';
type Phase = 'intro' | 'showcase' | 'runup' | 'hops' | 'arc' | 'between' | 'done';

const START_X = 150;
const RUNWAY_M = 22;
const PPM = SPRINT.pixelsPerMeter;
const HOP_TARGET = 0.5; // sweet spot on the 0..1 timing needle
const ATTEMPTS = 3;

/**
 * Triple Jump — mash the run-up, hit the board, then nail THREE rhythmic take-offs
 * (hop-step-jump). Each phase pops a sweeping timing needle: tap SALTA near the centre
 * for a long phase. Total = run-up speed × the three phase qualities. AI fill the field.
 */
export class TripleJump extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;

  private velocity = 0;
  private runDist = 0;
  private acc = 0;
  private lastSide: Side | null = null;
  private cadence = 0;
  private lastTapAt = 0;
  private maxSpeed: number = SPRINT.maxSpeed;
  private impulseScale = 1;
  private reach = 5;

  private foul = false;
  private hopIndex = 0;
  private hopQ: number[] = [];
  private needle = 0;
  private hopFromX = 0;
  private hopToX = 0;
  private arcT = 0;
  private arcDur = 0.5;
  private totalM = 0;
  private attempt = 1;
  private tripleBest = 0;
  private aiIndex = 0;
  private aiRig?: AthleteView;
  private playerMarker?: Phaser.GameObjects.GameObject[];

  private W = 0; private H = 0; private groundY = 0; private boardX = 0; private uiS = 1; private camTarget = 0;
  private aiJumps: { name: string; species: string; nationality: string; metric: number; skin: string }[] = [];

  private speedBar!: PowerMeter;
  private timeBar!: PowerMeter;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private jumpBtn!: Button;
  private tapZones!: TapZones;

  constructor() { super('TripleJump'); }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.velocity = 0; this.runDist = 0; this.acc = 0; this.cadence = 0; this.lastSide = null;
    this.foul = false; this.hopIndex = 0; this.hopQ = []; this.totalM = 0; this.camTarget = 0; this.hopTimer = undefined;
    this.attempt = 1; this.tripleBest = 0; this.aiIndex = 0; this.playerMarker = undefined;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.groundY = h - Math.max(96, h * 0.18);
    this.boardX = START_X + RUNWAY_M * PPM;
    const speedFactor = 0.7 + (this.athlete.stats.speed / 100) * 0.6;
    this.maxSpeed = SPRINT.maxSpeed * speedFactor;
    this.impulseScale = speedFactor;
    this.reach = 4 + (this.athlete.stats.power / 100) * 2.2;

    const src = this.textures.get('stadium').getSourceImage();
    this.add.image(0, 0, 'stadium').setOrigin(0, 0).setScrollFactor(0).setDepth(-10)
      .setScale(Math.max(w / src.width, (this.groundY + 30) / src.height));
    this.drawField();
    this.rig = createAthleteView(this, START_X, this.groundY, this.athlete, Phaser.Math.Clamp(h * 0.34, 170, 320));
    this.rig.container.setDepth(6);
    this.fetchWorldRecord('triple_jump', 'distance', (m) => this.drawGroundRecord(this.boardX + m * PPM, this.groundY, this.groundY - Math.max(70, this.H * 0.16), `${m.toFixed(2)}m`));

    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.aiJumps = opponents.map((o) => ({ name: o.name, species: o.species, nationality: o.nationality, skin: o.skin, metric: Math.max(8, o.jumpDistance * 2.1 + (Math.random() * 2.4 - 1.2)) }));

    this.buildHud();
    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
    this.controls.on('action', () => this.onAction());
    this.startCountdown();
  }

  private drawField(): void {
    const endX = this.boardX + 24 * PPM;
    // The hop + step land on the runway; only the final jump reaches the sand pit, so the
    // pit starts well past the board (real triple-jump layout) — not right at the board.
    this.sandStartX = this.boardX + 9 * PPM;
    const g = this.add.graphics().setDepth(-6);
    g.fillStyle(PALETTE.track, 1).fillRect(0, this.groundY, this.sandStartX, this.H - this.groundY);
    g.fillStyle(PALETTE.white, 1).fillRect(this.boardX - 6, this.groundY, 10, 22); // take-off board
    g.fillStyle(PALETTE.bad, 1).fillRect(this.boardX + 4, this.groundY, 4, 22); // foul line
    g.fillStyle(0xe8c98a, 1).fillRect(this.sandStartX, this.groundY, endX, this.H - this.groundY); // sand pit
    g.fillStyle(0xffffff, 0.7).fillRect(this.sandStartX - 2, this.groundY, 4, 18); // pit edge board
    for (let m = 2; m <= 22; m += 2) {
      const x = this.boardX + m * PPM;
      const onSand = x >= this.sandStartX;
      g.fillStyle(onSand ? 0xcaa463 : 0x5a6699, 1).fillRect(x, this.groundY, 2, 16);
      this.add.text(x, this.groundY + 20, `${m}`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '14px', color: onSand ? '#9a7b3e' : '#9fb0e0' }).setOrigin(0.5, 0).setDepth(-6);
    }
    this.cameras.main.setBounds(0, 0, endX + this.W, this.H);
  }

  private sandStartX = 0;

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, 16, t('game.hudRunup'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(26), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    const barW = Math.min(260, this.W * 0.42);
    const topY = Math.max(84, this.H * 0.15); // below the top-right quit button
    this.speedBar = new PowerMeter(this, 30 + barW / 2, topY, { width: barW, height: 18 });
    this.add.text(30 + barW / 2, topY - 18, t('hud.speed'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.timeBar = new PowerMeter(this, this.W - 30 - barW / 2, topY, { width: barW, height: 18, target: [HOP_TARGET - 0.12, HOP_TARGET + 0.12], fillColor: PALETTE.cadence });
    this.add.text(this.W - 30 - barW / 2, topY - 18, t('hud.timing'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.timeBar.setVisible(false);
    this.bigText = this.add.text(this.W / 2, this.H * 0.4, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(80), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);
    const label = this.sys.game.device.os.desktop ? `⬆ ${t('btn.jump')} ${t('btn.space')}` : `⬆ ${t('btn.jump')}`;
    this.jumpBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), label, () => this.onAction(), {
      width: Math.min(200, this.W * 0.4), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.good,
    });
    this.jumpBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
    this.tapZones = new TapZones(this);
    this.tapZones.setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 600, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go();
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 250, duration: 200 });
          this.runAiShowcase();
        } else AudioEngine.countdownTick();
      });
    });
  }

  /** Each rival's animal runs the runway and jumps to their best mark, then a flag. */
  private runAiShowcase(): void {
    this.phase = 'showcase';
    if (this.aiIndex >= this.aiJumps.length) { this.rig.container.setVisible(true); this.beginAttempt(); return; }
    this.rig.container.setVisible(false);
    const a = this.aiJumps[this.aiIndex]!;
    this.hud.setText(`🦘 ${a.name}`);
    this.aiRig?.destroy();
    this.aiRig = createAthleteView(this, START_X, this.groundY, { skin: a.skin }, Phaser.Math.Clamp(this.H * 0.34, 170, 320));
    this.aiRig.container.setDepth(6);
    this.aiRig.setMotion(0.9);
    const obj = { p: 0 };
    this.tweens.add({
      targets: obj, p: 1, duration: 950, ease: 'Sine.in',
      onUpdate: () => {
        const x = Phaser.Math.Linear(START_X, this.boardX, obj.p);
        this.aiRig!.container.x = x;
        this.aiRig!.update(1 / 60);
        this.camTarget = Phaser.Math.Clamp(this.boardX - this.W * 0.4, 0, this.cameras.main.getBounds().width - this.W);
      },
      onComplete: () => this.aiJumpArc(a),
    });
  }

  private aiJumpArc(a: { name: string; nationality: string; metric: number }): void {
    const landX = this.boardX + a.metric * PPM;
    this.aiRig!.setMotion(0);
    this.aiRig!.setPose('celebrate');
    const obj = { p: 0 };
    AudioEngine.go();
    this.tweens.add({
      targets: obj, p: 1, duration: 850, ease: 'Sine.out',
      onUpdate: () => {
        const x = Phaser.Math.Linear(this.boardX, landX, obj.p);
        this.aiRig!.container.x = x;
        this.aiRig!.container.y = this.groundY - Math.sin(obj.p * Math.PI) * this.H * 0.17;
        this.aiRig!.update(1 / 60);
        this.camTarget = Phaser.Math.Clamp(x - this.W * 0.4, 0, this.cameras.main.getBounds().width - this.W);
      },
      onComplete: () => {
        this.aiRig!.container.y = this.groundY;
        this.flagMarker(landX, PALETTE.cadence, `${countryFlag(a.nationality)} ${a.name.split(' ')[0]} · ${a.metric.toFixed(2)}m`, false);
        AudioEngine.thud();
        this.cameras.main.shake(80, 0.003);
        this.tweens.add({ targets: this.aiRig!.container, alpha: 0, duration: 250, onComplete: () => { this.aiRig?.destroy(); this.aiRig = undefined; this.aiIndex++; this.runAiShowcase(); } });
      },
    });
  }

  private beginAttempt(): void {
    this.velocity = 0; this.runDist = 0; this.acc = 0; this.cadence = 0; this.lastSide = null;
    this.foul = false; this.hopIndex = 0; this.hopQ = []; this.totalM = 0; this.needle = 0;
    this.phase = 'runup';
    this.bigText.setAlpha(0);
    this.rig.container.setVisible(true).setAlpha(1);
    this.rig.container.x = START_X; this.rig.container.y = this.groundY;
    this.rig.setMotion(0); this.rig.setPose('idle');
    this.controls.setEnabled(true);
    this.lastTapAt = this.time.now / 1000;
    this.jumpBtn.setText(this.sys.game.device.os.desktop ? `⬆ ${t('btn.jump')} ${t('btn.space')}` : `⬆ ${t('btn.jump')}`).setScale(1).setAlpha(0.92).setVisible(true);
    this.speedBar.setVisible(true);
    this.timeBar.setVisible(false);
    this.tapZones.setVisible(true);
    this.camTarget = Phaser.Math.Clamp(this.boardX - this.W * 0.4, 0, this.cameras.main.getBounds().width - this.W);
    this.cameras.main.scrollX = this.camTarget;
    this.hud.setText(t('game.attemptRunup', { n: this.attempt, max: ATTEMPTS }));
  }

  private flagMarker(x: number, color: number, label: string, mine: boolean): Phaser.GameObjects.GameObject[] {
    const poleH = Math.max(46, this.H * 0.11);
    const top = this.groundY - poleH;
    const g = this.add.graphics().setDepth(mine ? 6 : 4);
    g.fillStyle(0xffffff, 0.85).fillRect(x - 1.5, top, 3, poleH);
    g.fillStyle(color, 1).fillTriangle(x + 1.5, top, x + 1.5, top + 18, x + 26, top + 9);
    g.fillStyle(color, 1).fillCircle(x, this.groundY, 4);
    const t = this.add.text(x, top - 6, label, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(12 * this.uiS)}px`, color: '#0b1020', fontStyle: 'bold',
      backgroundColor: mine ? '#ffd24a' : '#bfe6ff', padding: { x: 5, y: 2 },
    }).setOrigin(0.5, 1).setDepth(mine ? 7 : 5);
    return [g, t];
  }

  private onTap(side: Side): void {
    if (this.phase !== 'runup') return;
    const now = this.time.now / 1000;
    const dt = Math.max(0.001, now - this.lastTapAt);
    this.cadence = Phaser.Math.Linear(this.cadence, 1 / dt, 0.45);
    this.lastTapAt = now;
    const alternated = side !== this.lastSide;
    this.lastSide = side;
    let mult = alternated ? 1 : SPRINT.sameSidePenalty;
    if (this.cadence >= SPRINT.idealCadenceMin && this.cadence <= SPRINT.idealCadenceMax && alternated) mult *= SPRINT.flowBonus;
    this.velocity = Math.min(this.maxSpeed * 1.06, this.velocity + SPRINT.tapImpulse * this.impulseScale * mult);
    this.tapZones.flash(side);
    AudioEngine.tap(alternated ? 0.6 : 0.2);
    this.vibrate(HAPTICS.tap);
  }

  /** ACTION: during run-up = take off (start hops); during a hop phase = lock the needle. */
  private onAction(): void {
    if (this.phase === 'runup') { this.takeoff(); return; }
    if (this.phase === 'hops') this.lockHop();
  }

  private takeoff(): void {
    const athleteX = START_X + this.runDist * PPM;
    this.foul = athleteX > this.boardX + 8;
    this.hopFromX = Math.min(athleteX, this.boardX);
    this.controls.setEnabled(true);
    this.speedBar.setVisible(false);
    this.tapZones.setVisible(false);
    if (this.foul) {
      this.jumpBtn.setVisible(false);
      this.cameras.main.flash(200, 255, 60, 60);
      this.totalM = 0;
      this.land();
      return;
    }
    this.phase = 'hops';
    this.hopIndex = 0;
    this.hopQ = [];
    this.timeBar.setVisible(true);
    // Keep the action button visible through the three hops (relabelled) — it's how you lock
    // each take-off on mobile; it pulses when the timing needle is in the green.
    this.jumpBtn.setText(this.sys.game.device.os.desktop ? `⬆ ${t('btn.detach')} ${t('btn.space')}` : `⬆ ${t('btn.detach')}`).setVisible(true);
    this.startHop();
  }

  private startHop(): void {
    this.needle = 0;
    this.hud.setText(t('game.hopPhase', { phase: ['HOP', 'STEP', 'JUMP'][this.hopIndex]!, n: this.hopIndex + 1 }));
    AudioEngine.beat();
    // Auto-lock if the player hesitates too long — bound to THIS hop index and cancelled
    // on a manual lock, so a previous hop's timer can never fire on a later one.
    this.hopTimer?.remove();
    const idx = this.hopIndex;
    this.hopTimer = this.time.delayedCall(1700, () => {
      if (this.phase === 'hops' && this.hopIndex === idx && this.hopQ.length === idx) this.lockHop();
    });
  }

  private hopTimer?: Phaser.Time.TimerEvent;

  private lockHop(): void {
    if (this.hopQ.length !== this.hopIndex) return;
    this.hopTimer?.remove();
    this.hopTimer = undefined;
    const q = Phaser.Math.Clamp(1 - Math.abs(this.needle - HOP_TARGET) / 0.5, 0.2, 1);
    this.hopQ.push(q);
    const speedNorm = Phaser.Math.Clamp(this.velocity / this.maxSpeed, 0, 1);
    const hopM = (1.4 + speedNorm * this.reach) * q;
    this.hopToX = this.hopFromX + hopM * PPM;
    this.totalM += hopM;
    this.arcT = 0;
    this.arcDur = Phaser.Math.Clamp(hopM / 9, 0.35, 0.8);
    this.phase = 'arc';
    this.timeBar.setVisible(false);
    this.jumpBtn.setScale(1).setAlpha(0.92);
    this.rig.setMotion(0);
    this.rig.setPose('celebrate');
    AudioEngine.go();
    this.vibrate(q > 0.8 ? HAPTICS.goodTap : HAPTICS.tap);
    this.bigText.setText(q > 0.85 ? t('game.perfect') : q > 0.55 ? t('game.good') : t('game.short')).setColor(q > 0.55 ? '#4ade80' : '#ffd24a').setAlpha(1).setScale(0.7);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 220, yoyo: true, hold: 80, onComplete: () => this.bigText.setAlpha(0) });
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.cameras.main.scrollX = Phaser.Math.Linear(this.cameras.main.scrollX, this.camTarget, 0.12);
    if (this.phase === 'intro' || this.phase === 'done' || this.phase === 'showcase' || this.phase === 'between') { this.rig.update(dt); return; }
    if (this.phase === 'runup') { this.updateRunup(dt); return; }
    if (this.phase === 'hops') { this.updateHops(dt); return; }
    if (this.phase === 'arc') { this.updateArc(dt); }
  }

  private updateRunup(dt: number): void {
    this.acc += dt;
    let steps = 0;
    while (this.acc >= SIM_STEP && steps < MAX_SIM_STEPS_PER_FRAME) {
      this.velocity *= SPRINT.dragPerStep;
      this.runDist += this.velocity * SIM_STEP;
      this.acc -= SIM_STEP; steps++;
    }
    const worldX = START_X + this.runDist * PPM;
    if (worldX > this.boardX + 3 * PPM) { this.takeoff(); return; }
    this.rig.container.x = worldX; this.rig.container.y = this.groundY;
    const sn = Phaser.Math.Clamp(this.velocity / this.maxSpeed, 0, 1);
    this.rig.setMotion(sn); this.rig.update(dt);
    this.speedBar.set(sn);
    this.tapZones.update(dt);
    this.camTarget = Phaser.Math.Clamp(this.boardX - this.W * 0.4, 0, this.cameras.main.getBounds().width - this.W);
    const toBoard = (this.boardX - worldX) / PPM;
    this.hud.setText(toBoard > 0 ? t('game.boardIn', { m: toBoard.toFixed(1) }) : t('game.jumpNow'));
    if (this.time.now / 1000 - this.lastTapAt > SPRINT.cadenceWindow) this.cadence = Phaser.Math.Linear(this.cadence, 0, 0.08);
  }

  private updateHops(dt: number): void {
    // Needle sweeps back and forth; the player taps to lock near the centre band.
    this.needle = 0.5 + 0.5 * Math.sin(this.time.now / 1000 * 4.2);
    this.timeBar.set(this.needle, this.needle);
    // Pulse the button when the needle is in the green sweet spot (press now!).
    const near = Math.abs(this.needle - HOP_TARGET) < 0.12;
    this.jumpBtn.setScale(near ? 1.12 : 1).setAlpha(near ? 1 : 0.92);
    this.rig.update(dt);
    this.camTarget = Phaser.Math.Clamp(this.hopFromX - this.W * 0.4, 0, this.cameras.main.getBounds().width - this.W);
  }

  private updateArc(dt: number): void {
    this.arcT += dt;
    const p = Phaser.Math.Clamp(this.arcT / this.arcDur, 0, 1);
    const x = Phaser.Math.Linear(this.hopFromX, this.hopToX, p);
    const arc = Math.sin(p * Math.PI) * (this.H * 0.12);
    this.rig.container.x = x;
    this.rig.container.y = this.groundY - arc;
    this.rig.update(dt);
    this.camTarget = Phaser.Math.Clamp(x - this.W * 0.4, 0, this.cameras.main.getBounds().width - this.W);
    if (p >= 1) {
      this.hopFromX = this.hopToX;
      this.rig.container.y = this.groundY;
      this.cameras.main.shake(60, 0.003);
      this.hopIndex++;
      if (this.hopIndex >= 3) { this.land(); return; }
      // Footprint puff at this bound's landing so the three take-offs read clearly.
      const onSand = this.hopFromX >= this.sandStartX;
      const puff = this.add.particles(this.hopFromX, this.groundY, 'spark', {
        lifespan: 420, speed: { min: 20, max: 90 }, angle: { min: 210, max: 330 },
        scale: { start: 0.4, end: 0 }, alpha: { start: 0.5, end: 0 }, tint: onSand ? 0xcaa463 : 0x8b95c0, gravityY: 260, emitting: false,
      }).setDepth(5);
      puff.explode(8);
      this.time.delayedCall(600, () => puff.destroy());
      this.phase = 'hops';
      this.timeBar.setVisible(true);
      this.rig.setPose('idle');
      this.startHop();
    }
  }

  private land(): void {
    this.phase = 'between';
    this.controls.setEnabled(false);
    this.tapZones.setVisible(false);
    this.timeBar.setVisible(false);
    this.jumpBtn.setScale(1).setVisible(false);
    const dist = this.foul ? 0 : this.totalM;
    const landX = this.boardX + dist * PPM;
    this.cameras.main.shake(140, 0.005);
    AudioEngine.thud();
    this.vibrate(HAPTICS.finish);

    const isBest = !this.foul && dist > this.tripleBest;
    if (!this.foul) {
      const dust = this.add.particles(landX, this.groundY, 'spark', {
        lifespan: 520, speed: { min: 30, max: 130 }, angle: { min: 200, max: 340 },
        scale: { start: 0.5, end: 0 }, alpha: { start: 0.6, end: 0 }, tint: 0xcaa463, gravityY: 320, emitting: false,
      });
      dust.explode(14);
      this.time.delayedCall(800, () => dust.destroy());
    }
    if (isBest) {
      this.tripleBest = dist;
      this.playerMarker?.forEach((o) => o.destroy());
      this.playerMarker = this.flagMarker(landX, PALETTE.sun, `${countryFlag(this.athlete.nationality)} ${this.athlete.name.split(' ')[0]} · ${this.tripleBest.toFixed(2)}m`, true);
      AudioEngine.fanfare();
    }
    this.bigText.setText(this.foul ? t('game.foul') : `${dist.toFixed(2)} m${isBest ? '  ⭐' : ''}`).setColor(this.foul ? '#ff5d6c' : '#ffd24a').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 450, ease: 'Back.out' });
    this.hud.setText(t('game.attemptBest', { n: this.attempt, max: ATTEMPTS, m: this.tripleBest.toFixed(2) }));

    if (this.attempt < ATTEMPTS) {
      this.attempt++;
      this.time.delayedCall(1700, () => this.beginAttempt());
    } else {
      this.time.delayedCall(1900, () => this.finalize());
    }
  }

  private finalize(): void {
    this.phase = 'done';
    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: this.tripleBest },
      ...this.aiJumps.map((a) => ({ name: a.name, species: a.species, isPlayer: false, nationality: a.nationality, metric: a.metric })),
    ].sort((x, y) => y.metric - x.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp(this.tripleBest * 58, 0, 1000));
    const reward = getDiscipline('triple_jump').scoreToReward(score);
    const result: DisciplineResult = {
      disciplineId: 'triple_jump', metricKind: 'distance', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: this.tripleBest, score, reward, rank, standings,
    };
    void this.finishDiscipline(result);
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
